import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { PROCESS_STEPS } from '@/constants'
import { useInView } from '@helpers/useIntersectionObserver'

const AUTOPLAY_DELAY = 2500
// const CARD_GAP_PX = 28
const DRAG_THRESHOLD = 80
const MANUAL_PAUSE_MS = 5000

function useVisibleCount() {
  const [visibleCount, setVisibleCount] = useState(3)
  useEffect(() => {
    function calc() {
      const w = window.innerWidth
      if (w < 768) setVisibleCount(0)
      else if (w < 992) setVisibleCount(2)
      else setVisibleCount(3)
    }
    calc()
    window.addEventListener('resize', calc)
    return () => window.removeEventListener('resize', calc)
  }, [])
  return visibleCount
}

export default function WorkingProcess() {
  const visibleCount = useVisibleCount()
  const total = PROCESS_STEPS.length
  const maxStart = Math.max(0, total - visibleCount)

  const [startIndex, setStartIndex] = useState(0)
  const [paused, setPaused]         = useState(false)
  const [sectionRef, inView]        = useInView({ threshold: 0.3 })
  const intervalRef  = useRef(null)
  const resumeRef    = useRef(null)   // ← 5s resume timer
  const dragStartX   = useRef(null)   // ← drag start position

  useEffect(() => { setStartIndex(0) }, [visibleCount])

   // Autoplay — existing, unchanged
  useEffect(() => {
    if (!visibleCount || paused || !inView) return
    intervalRef.current = setInterval(() => {
      setStartIndex(prev => (prev >= maxStart ? 0 : prev + 1))
    }, AUTOPLAY_DELAY)
    return () => clearInterval(intervalRef.current)
  }, [visibleCount, paused, inView, maxStart])

  // ── Manual navigation helper — pauses for 5s then resumes
  const manualNav = (nextIndex) => {
    const clamped = Math.max(0, Math.min(nextIndex, maxStart))
    setStartIndex(clamped)
    // Pause autoplay temporarily
    clearInterval(intervalRef.current)
    clearTimeout(resumeRef.current)
    setPaused(true)
    resumeRef.current = setTimeout(() => setPaused(false), MANUAL_PAUSE_MS)
  }

  const goPrev = () => manualNav(startIndex - 1)
  const goNext = () => manualNav(startIndex === maxStart ? 0 : startIndex + 1)

  // ── Drag handlers
  const onDragStart = (e) => {
    dragStartX.current = e.type === 'touchstart'
      ? e.touches[0].clientX
      : e.clientX
  }

  const onDragEnd = (e) => {
    if (dragStartX.current === null) return
    const endX = e.type === 'touchend'
      ? e.changedTouches[0].clientX
      : e.clientX
    const delta = dragStartX.current - endX
    dragStartX.current = null
    if (Math.abs(delta) < DRAG_THRESHOLD) return
    delta > 0 ? goNext() : goPrev()
  }

  // Cleanup resume timer on unmount
  useEffect(() => () => {
    clearTimeout(resumeRef.current)
  }, [])

  const isSlider  = visibleCount > 0
  // const cardBasis = isSlider ? `calc(${100 / total}% - ${CARD_GAP_PX}px)` : undefined
  // const trackWidth = isSlider ? `${(total / visibleCount) * 100}%` : undefined
  // const translateX = isSlider ? -(startIndex * (100 / total)) : 0

  // Dots — one per valid startIndex position
  const dotCount = maxStart + 1

  return (
    <section
      id="working-process"
      className="section-py"
      aria-labelledby="process-heading"
      ref={sectionRef}
    >
      <div className="container">
        <div className="section-header" data-aos="fade-up">
          <span className="process-eyebrow">OUR PROCESSES</span>
          <h2 id="process-heading" className="process-heading">
            <span className="process-heading-highlight">Streamlined Tech Process</span>
          </h2>
          <p className="section-subheading">
            Adhering to processes is the back bone of our success story.           
          </p>
        </div>

        <div
          className="process-viewport"
          data-visible={visibleCount}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onMouseDown={isSlider ? onDragStart : undefined}
          onMouseUp={isSlider ? onDragEnd : undefined}
          onTouchStart={isSlider ? onDragStart : undefined}
          onTouchEnd={isSlider ? onDragEnd : undefined}
          //style={isSlider ? { cursor: 'grab', userSelect: 'none' } : undefined}
          style={isSlider ? {
  cursor: 'grab',
  userSelect: 'none',
  '--process-visible': visibleCount,
  '--process-index': startIndex,
} : undefined}
        >
          {isSlider && <span className="process-line" aria-hidden="true" />}

          <div
            className="process-track"
            // style={isSlider ? {
            //   width: trackWidth,
            //   transform: `translateX(${translateX}%)`,
            // } : undefined}
          >
            {!isSlider && <span className="process-line" aria-hidden="true" />}
            {PROCESS_STEPS.map((step, i) => (
              <div
                key={step.number}
                className="process-card"
               // style={isSlider ? { flex: `0 0 ${cardBasis}` } : undefined}
                data-aos="fade-up"
                data-aos-delay={i * 90}
              >
                <span className="process-watermark" aria-hidden="true">{step.number}</span>
                <span className="process-badge">{step.number}</span>
                <h3 className="process-title">{step.title}</h3>
                <p className="process-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Controls: arrows + dots — hidden on mobile ── */}
        {isSlider && (
          <div className="process-controls" aria-label="Slider controls">

            {/* Prev arrow */}
            <button
              className="process-arrow process-arrow--prev"
              onClick={goPrev}
              aria-label="Previous slide"
              disabled={startIndex === 0}
            >
              <ChevronLeft size={18} aria-hidden="true" />
            </button>

            {/* Dots */}
            <div className="process-dots" role="tablist" aria-label="Slide position">
              {Array.from({ length: dotCount }).map((_, i) => (
                <button
                  key={i}
                  role="tab"
                  aria-selected={i === startIndex}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`process-dot ${i === startIndex ? 'process-dot--active' : ''}`}
                  onClick={() => manualNav(i)}
                />
              ))}
            </div>

            {/* Next arrow */}
            <button
              className="process-arrow process-arrow--next"
              onClick={goNext}
              aria-label="Next slide"
            >
              <ChevronRight size={18} aria-hidden="true" />
            </button>

          </div>
        )}

      </div>
    </section>
  )
}